import React, { useCallback, useRef, useState } from 'react'
import { Buffer } from 'buffer'
import { usePapaParse } from 'react-papaparse'
import { Link, useLocation } from 'react-router-dom'
import { PageSection, Title } from '@patternfly/react-core'
import { Button, Icon, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'design-react-kit'
import styled from 'styled-components'

import { generateRequestFilter, generateRequestSort } from 'src/app/utils/ApiUtils'
import { useUserEnte } from 'src/hooks/useReqOptions'
import useFrontOffice from 'src/hooks/useFrontOffice'
import { GenericAsyncTableData, RequestData } from 'src/backoffice/app/components/GenericAsyncTableData'
import {
  PATH_TO_BO_ELENCO_EVENTI_CATALOGO,
  PATH_TO_BO_ISCRITTI_EVENTO_CATALOGO,
  PATH_TO_FO_ELENCO_EVENTI_CATALOGO,
  PATH_TO_FO_ISCRITTI_EVENTO_CATALOGO,
} from 'src/app/utils/RoutesConstants'

import { getDefaultAxiosConfig, getFormazioneResourceApi } from 'src/app/utils/ManagerRestGateway'
import GenericBreadCrumb from 'src/frontend/app/components/GenericBreadCrumb'
import GenericUpload from 'src/frontend/app/components/Form/Upload'
import AssegnaCFP from './formEventiCatalogo/AssegnaCFP'
import moment from 'moment'
import { TokenStore } from 'src/keycloak/jwt/TokenStore'
import { responseMapperEvento } from './formEntiEsterni/formUtils'
import { getIscrizioneOrdineByCfBO } from 'src/processes/Soggetto'
import { goTo, isEmptyValue } from 'src/utilities/utility'
import { getEventoACatalogoById } from 'src/processes/formazione/EventoService'

const LinkStyled = styled.div`
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: end;
`
const Spacer = () => (
  <>
    <br></br>
  </>
)

const IscrittiEventoCatalogo: React.FunctionComponent = () => {
  const isFrontOffice = useFrontOffice()
  const pathToElenco = isFrontOffice ? PATH_TO_FO_ELENCO_EVENTI_CATALOGO : PATH_TO_BO_ELENCO_EVENTI_CATALOGO
  const pathToIscritti = isFrontOffice ? PATH_TO_FO_ISCRITTI_EVENTO_CATALOGO : PATH_TO_BO_ISCRITTI_EVENTO_CATALOGO
  const basePath = { label: 'Elenco Eventi a Catalogo', link: pathToElenco }
  const [popoverOpenInfo, setPopoverInfo] = useState(false)
  const targetRefInfo = useRef(null)
  const myPath = {
    label: 'Iscritti Evento a Catalogo',
    link: pathToIscritti,
  }

  const [manualRefreshCount, setManualRefreshCount] = useState(0)
  const { readString, jsonToCSV } = usePapaParse()

  const [evento, setEvento] = useState<any>(null)
  const [isFadAsinc, setIsFadAsinc] = useState<boolean>(false)

  const userEnte = useUserEnte()
  const [enti, setListEnti] = useState<any>([])

  const isOrganizzatore = userEnte?.codiceEnte === evento?.organizzatoda
  const [data, setData] = useState<any>({})

  const location = useLocation<{ data: any }>()

  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get('id')

  const [modaleCFP, setModaleCFP] = useState<any>(null)

  const loadData = useCallback(
    async (request: RequestData) => {
      try {
        const { data } = await getEventoACatalogoById(id)
        if (data?.modalita?.value === 'FAD_ASINCRONO') {
          setIsFadAsinc(true)
        }
        const _data = await responseMapperEvento(data)
        //Ulteriore controllo per capire se l'evento è approvato
        if (isEmptyValue(_data.evento) || isEmptyValue(_data.evento.accreditato) || _data.evento.accreditato !== 1) {
          alert('Questo evento non è approvato!')
          goTo(pathToElenco)
        }

        let max = null
        if (_data.evento && _data.evento.data_inizio && _data.evento.data_fine) {
          const diffInDays = moment(_data.evento.data_fine).diff(moment(_data.evento.data_inizio), 'days')
          max = Math.floor(diffInDays)
        }

        if (!_data.tipologiaAttivita) {
          _data.crediti_riconosciuti = 0
        } else {
          if (_data.tipologiaAttivita.um === 'H') {
            const totOre = _data.totOreCFU
            if (totOre && totOre.indexOf(':') > 0) {
              const ore = parseInt(totOre.split(':')[0])
              const minuti = parseInt(totOre.split(':')[1])
              _data.crediti_riconosciuti = ((ore + minuti / 60) / _data.tipologiaAttivita.ore_evt_battute) * _data.tipologiaAttivita.max_cfp_evt
            } else {
              _data.crediti_riconosciuti = _data.tipologiaAttivita.max_cfp_evt
            }

            if (max) {
              _data.crediti_riconosciuti = _data.crediti_riconosciuti > max ? max : _data.crediti_riconosciuti
            }
          } else {
            if (_data.tipologiaAttivita.cod === 'AO' || _data.tipologiaAttivita.cod === 'AF') {
              _data.crediti_riconosciuti = 0.25
            } else {
              _data.crediti_riconosciuti = _data.evento.crediti ? _data.evento.crediti : _data.tipologiaAttivita.max_cfp_evt
            }
          }
        }
        _data.crediti_riconosciuti = _data.evento && _data.evento.crediti ? _data.evento.crediti : _data.crediti_riconosciuti
        setData(_data)
      } catch (e) {
        alert("Si è verificato un problema durante l'apertura della pagina!")
        goTo(pathToElenco)
      }

      const corso = { field: 'idcorso', value: id }
      const { data: evento } = await getFormazioneResourceApi().msfoFormazioneListaEventiACatalogoGet(
        generateRequestFilter([corso]),
        1,
        0,
        undefined,
        getDefaultAxiosConfig(),
      )
      setEvento(evento?.items?.[0])

      request.filters.push(corso)
      const { data } = await getFormazioneResourceApi().msfoFormazioneListaIscrittiGet(
        generateRequestFilter(request.filters),
        request.pageSize,
        request.currentPage,
        generateRequestSort(request.sortColumn, request.sortDirection),
        getDefaultAxiosConfig(),
      )

      return {
        items: data.items || [],
        total: data.count || 0,
      }
    },
    [id],
  )

  const loadAllData = async (id) => {
    const corso = { field: 'idcorso', value: id }
    const { data } = await getFormazioneResourceApi().msfoFormazioneListaIscrittiGet(
      generateRequestFilter([corso]),
      10000,
      0,
      undefined,
      getDefaultAxiosConfig(),
    )
    return data.items || []
  }

  const generateSignatures = useCallback(async () => {
    let data = await loadAllData(id)

    const mappedData = data.map((item: any) => ({
      COGNOME: item.cognome || '',
      NOME: item.nome || '',
      'CODICE FISCALE': item.cf || '',
      EMAIL: item.email || '',
      ORDINE: item.ordine_territoriale_competente || '',
      'N.ISCRIZIONE': item.numero_iscrizione_ordine || '',
      'ORA ENTRATA': '',
      'ORA USCITA': '',
      'FIRMA USCITA': '',
      'FIRMA PRIVACY': '',
    }))

    const exported = jsonToCSV(mappedData, { delimiter: ';' })

    const BOM = '\uFEFF'
    const blob = new Blob([BOM + exported], { type: 'text/csv;charset=utf-8;' })

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'foglio_firma.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }, [id])

  const assignCFU = useCallback(
    async (_, { allegatoDTO }) => {
      const { data: eventoData } = await getEventoACatalogoById(id)
      const evento = eventoData
      const iscritti = await loadAllData(id)

      const decodedString = Buffer.from(allegatoDTO?.contentBase64, 'base64').toString('utf-8')

      readString(decodedString, {
        worker: true,
        skipEmptyLines: true,
        complete: async (results: any) => {
          const validResults: any[] = []
          const invalidResults: any[] = []
          const isCFPMassiva = true

          for (let i = 0; i < (results?.data || []).length; i++) {
            const r = results.data[i]
            const riga = `(Riga ${i + 1})`

            const idNaz = r[0]?.trim()
            const value = r[1]?.trim()
            const user_name = r[2]?.trim()
            const cfpDecimali = r[3]?.trim()

            let anno: number | null = null
            let annoCfpFad: String | null = null

            const numero = parseFloat(cfpDecimali)
            const cfp = isNaN(numero) ? null : parseFloat(numero.toFixed(3))

            if (isFadAsinc) {
              if (!moment(value, 'DD-MM-YYYY', true).isValid()) {
                invalidResults.push({
                  message: `${riga} La data di fruizione deve essere nel formato GG-MM-AAAA`,
                })
                continue
              }

              annoCfpFad = moment(value, 'DD-MM-YYYY').format('YYYY-MM-DD')
            } else {
              anno = parseInt(value)

              if (!anno || isNaN(anno)) {
                invalidResults.push({
                  message: `${riga} L'anno deve essere un numero valido`,
                })
                continue
              }
            }

            if (!idNaz || !user_name || !cfp) {
              invalidResults.push({
                message: `${riga} Devono essere presenti: id evento, anno/data, codice fiscale e cfp`,
              })
              continue
            }

            if (idNaz !== evento.evento.id_naz.toString()) {
              invalidResults.push({
                message: `${riga} Il codice evento ${idNaz} non corrisponde a quello selezionato ${evento.evento.id_naz}`,
              })
              continue
            }

            let item = iscritti.find((i: any) => (i.user_name ? i.user_name.toLowerCase() : '') === (user_name ? user_name.toLowerCase() : ''))

            if (!item) {
              try {
                const response = await getIscrizioneOrdineByCfBO(user_name.toUpperCase())

                item = {
                  cf: user_name,
                  cognome: response.data.returnedObject.cognome,
                  nome: response.data.returnedObject.nome,
                  idcorso: id,
                }
              } catch {
                invalidResults.push({
                  message: `${riga} Nessuna corrispondenza trovata nell'albo per il codice fiscale ${user_name}`,
                })
                continue
              }
            }

            const updatedItem = {
              ...item,
              user_name: item?.cf,
              cfp,
              anno,
              annoCfpFad,
            }

            validResults.push(updatedItem)
          }

          setModaleCFP({
            validResults,
            invalidResults,
            isCFPMassiva,
          })
        },
      })
    },
    [id, isFadAsinc],
  )

  const filters: any = [
    { label: 'Cognome', field: 'cognome', type: 'input', operator: 'LIKE' },
    { label: 'Nome', field: 'nome', type: 'input', operator: 'LIKE' },
    { label: 'Codice Fiscale', field: 'cf', type: 'input', operator: 'LIKE' },
    { label: 'Ordine di appartenenza', field: 'ordine_territoriale_competente', type: 'input', operator: 'LIKE' },
    { label: 'CFP assegnati', field: 'cfpassegnati', type: 'input', operator: 'LIKE' },
    { label: 'N. iscrizione', field: 'numero_iscrizione_ordine', type: 'input', operator: 'LIKE' },
  ]

  return (
    <PageSection>
      <div className="container  register-container">
        <GenericBreadCrumb paths={[basePath, myPath]} />
        <Spacer />

        <Title headingLevel="h1" size="lg">
          {myPath.label}
        </Title>
        <Spacer />
        {(TokenStore.getInstance().isOperatoreCONAF() || isOrganizzatore) && (
          <div className="d-flex justify-content-end mb-4">
            {!TokenStore.getInstance().isCurrentAccountFormatore() && (
              <div className="me-4">
                <Label className="d-flex justify-content-center" for="massive_cfp">
                  <strong className="me-2">Assegnazione massiva CFP</strong>
                  <a>
                    <Icon
                      onClick={() => {
                        setPopoverInfo(!popoverOpenInfo)
                      }}
                      color="primary"
                      icon="it-info-circle"
                      size="sm"
                    />
                  </a>
                </Label>

                <GenericUpload id="massive_cfp" extensions={['csv']} accept={['text/csv', 'csv']} onChange={assignCFU} />
              </div>
            )}
            <div className="me-2">
              <Button
                color="secondary"
                onClick={(e) => {
                  e.preventDefault()
                  generateSignatures()
                }}
              >
                Generazione foglio firma
              </Button>
            </div>
            <div>
              <Modal
                placement={'bottom'}
                isOpen={popoverOpenInfo}
                target={targetRefInfo}
                toggle={() => {
                  setPopoverInfo(!popoverOpenInfo)
                }}
                size="lg"
              >
                <ModalHeader icon="it-info-circle" id="assegnaCFUMassivi">
                  Assegnazione massiva CFP
                </ModalHeader>
                <ModalBody>
                  <Label>
                    Tramite tale funzione l’organizzatore dell’evento (e solo lui), ha la possibilità di assegnare i crediti formativi ai partecipanti
                    all’evento in modo massivo tramite caricamento di un file di import (.csv)
                  </Label>
                  <Label>
                    Per ciascuna riga del file di import devono essere riportati i seguenti dati separati da “,”:
                    <li>Id evento</li>
                    {isFadAsinc ? <li>Data di fruizione (GG-MM-AAAA)</li> : <li>Anno di svolgimento</li>}
                    <li>Codice fiscale iscritto (in maiuscolo)</li>
                    <li>CFP assegnati (utilizzare il punto come separatore dei decimali: 3.85, 1.075, etc)</li>
                  </Label>
                  <Label>Nel file csv caricato non deve essere presente alcuna riga di intestazione.</Label>
                </ModalBody>
                <ModalFooter>
                  <Button color="primary" onClick={() => setPopoverInfo(!popoverOpenInfo)}>
                    Ok
                  </Button>
                </ModalFooter>
              </Modal>
            </div>
          </div>
        )}
        <GenericAsyncTableData
          manualRefreshCount={manualRefreshCount}
          emptyMessage={'Nessun iscritto trovato'}
          filters={filters}
          columns={[
            { label: 'Cognome', field: 'cognome', isSortable: true },
            { label: 'Nome', field: 'nome', isSortable: true },
            { label: 'Codice Fiscale', field: 'cf', isSortable: true },
            { label: 'Email', field: 'email', isSortable: false },
            { label: 'Ordine di appartenenza', field: 'ordine_territoriale_competente', isSortable: true },
            { label: 'CFP assegnati', field: 'cfpassegnati', isSortable: true, toFixed: 3 },
            { label: 'N. iscrizione', field: 'numero_iscrizione_ordine', isSortable: true },
          ]}
          getRows={loadData}
          getRowActions={(row) => {
            const actions: any = TokenStore.getInstance().isCurrentAccountFormatore()
              ? []
              : [{ label: 'Assegna crediti', onClick: () => setModaleCFP({ validResults: [{ ...row, user_name: row.cf }] }) }]
            return actions.filter((a) => a)
          }}
        />
        {modaleCFP && (
          <AssegnaCFP
            iscritti={modaleCFP?.validResults}
            errori={modaleCFP?.invalidResults}
            evento={evento}
            onClose={() => {
              setModaleCFP(null)
              setManualRefreshCount((c) => c + 1)
            }}
            maxCFPCorso={data.crediti_riconosciuti}
            cfpMassiva={modaleCFP?.isCFPMassiva}
          />
        )}
        <LinkStyled>
          <Link to={pathToElenco}>Torna alla lista</Link>
        </LinkStyled>
      </div>
    </PageSection>
  )
}

export default IscrittiEventoCatalogo
